import PageFrame from './components/PageFrame';
import UpdateBanner from './components/UpdateBanner';
// src/App.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Layout from './components/Layout';
import SplashScreen from './components/SplashScreen';

import Home from './pages/Home';
import LabsPage from './pages/LabsPage';
import InstallApp from './pages/InstallApp';
import LabDetailPage from './pages/LabDetailPage';
import RouteSeo from './components/RouteSeo';
import Profile from './pages/Profile';
import Trade from './pages/Trade';
import DepositWithdraw from './pages/DepositWithdraw';
import Transactions from './pages/Transactions';
import GoldRequest from './pages/GoldRequest';
import EditProfile from './pages/EditProfile';
import Referral from './pages/Referral';
import Security from './pages/Security';
import BankAccountsPage from './pages/BankAccountsPage';
import About from './pages/About';
import WalletRequests from './pages/WalletRequests';
import Tutorial from './pages/Tutorial';
import Faq from './pages/FAQ';
import Contact from './pages/Contact';
import PriceChart from './pages/PriceChart';
import VersionInfo from './pages/VersionInfo';

import AdminPanel from './pages/AdminPanel';
import AdminGrades from './pages/AdminGrades';
import Prices from './pages/Prices';
import AdminGoldRequests from './pages/AdminGoldRequests';
import AdminWalletRequests from './pages/AdminWalletRequests';
import AdminUserBalances from './pages/AdminUserBalances';
import AdminGoldTransactions from './pages/AdminGoldTransactions';
import AdminChargeAccount from './pages/AdminChargeAccount';
import Notifications from './pages/Notifications';
import ActivityTracker from './components/ActivityTracker';
import PushNotice from './components/PushNotice';
import AdminActivity from './pages/AdminActivity';
import AdminCRM from './pages/AdminCRM';
import CustomerDocuments from './pages/CustomerDocuments';
import AdminPriceSettings from './pages/AdminPriceSettings';
import AdminChatPanel from './pages/AdminChatPanel';

import Login from './pages/Login';
import Register from './pages/Register';
import ChatPage from './pages/ChatPage';

// PWA
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { toast } from 'react-toastify';

// تشخیص مرورگر
function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // پایان اسپلش
  const handleSplashComplete = useCallback(() => {
    setShowSplash(false);
  }, []);

  // آماده‌سازی اپ
  useEffect(() => {
    if (!showSplash) {
      setIsReady(true);
    }
  }, [showSplash]);

  useEffect(() => serviceWorkerRegistration.subscribeToUpdates(setShowUpdateBanner), []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      const result = await serviceWorkerRegistration.forceUpdate();
      if (!result.updated) toast.info('نسخه جدیدی آماده نیست؛ سایت شما به‌روز است.');
    } catch {
      toast.error('به‌روزرسانی انجام نشد؛ اتصال اینترنت را بررسی و دوباره تلاش کنید.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      {/* اسپلش اسکرین */}
      <AnimatePresence>
        {showSplash && (
          <SplashScreen
            key="splash"
            onComplete={handleSplashComplete}
            minDuration={3000}
          />
        )}
      </AnimatePresence>

      {/* اپ اصلی */}
      {isReady && !showSplash && (
        <Router>
          <ActivityTracker />
          <PushNotice />
          <RouteSeo />
          {/* بنر بروزرسانی زیبا */}
          {showUpdateBanner && (
            <UpdateBanner
              isUpdating={isUpdating}
              onUpdate={handleUpdate}
              onDismiss={() => setShowUpdateBanner(false)}
            />
          )}

          {/* روت‌ها */}
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} />
                <Route path="install" element={<InstallApp />} />
                <Route path="price-chart" element={<PriceChart />} />
                <Route path="profile_page" element={<Profile />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="trade_page" element={<Trade />} />
                <Route path="deposit-withdraw_page" element={<DepositWithdraw />} />
                <Route path="transactions_page" element={<Transactions />} />
                <Route path="gold-request_page" element={<PageFrame page="GoldRequest"><GoldRequest /></PageFrame>} />
                <Route path="edit-profile_page" element={<PageFrame page="EditProfile"><EditProfile /></PageFrame>} />
                <Route path="referral_page" element={<PageFrame page="Referral"><Referral /></PageFrame>} />
                <Route path="security_page" element={<PageFrame page="Security"><Security /></PageFrame>} />
                <Route path="bank-accounts_page" element={<PageFrame page="BankAccountsPage"><BankAccountsPage /></PageFrame>} />
                <Route path="about_page" element={<PageFrame page="About"><About /></PageFrame>} />
                <Route path="wallet-requests_page" element={<PageFrame page="WalletRequests"><WalletRequests /></PageFrame>} />
                <Route path="Contact" element={<PageFrame page="Contact"><Contact /></PageFrame>} />
                <Route path="version-info" element={<VersionInfo />} />

                <Route path="AdminPanel_page" element={<PageFrame page="AdminPanel"><AdminPanel /></PageFrame>} />
                <Route path="admin-activity_page" element={<PageFrame page="AdminActivity"><AdminActivity /></PageFrame>} />
                <Route path="admin-crm_page" element={<PageFrame page="AdminCRM"><AdminCRM /></PageFrame>} />
                <Route path="customer-documents_page" element={<PageFrame page="CustomerDocuments"><CustomerDocuments /></PageFrame>} />
                <Route path="customer-documents_page/:id" element={<PageFrame page="CustomerDocuments"><CustomerDocuments /></PageFrame>} />
                <Route path="admin-grades_page" element={<PageFrame page="AdminGrades"><AdminGrades /></PageFrame>} />
                <Route path="prices_page" element={<PageFrame page="Prices"><Prices /></PageFrame>} />
                <Route path="admin-gold-requests_page" element={<PageFrame page="AdminGoldRequests"><AdminGoldRequests /></PageFrame>} />
                <Route path="AdminWalletRequests_page" element={<PageFrame page="AdminWalletRequests"><AdminWalletRequests /></PageFrame>} />
                <Route path="AdminUserBalances_page" element={<PageFrame page="AdminUserBalances"><AdminUserBalances /></PageFrame>} />
                <Route path="AdminGoldTransactions_page" element={<PageFrame page="AdminGoldTransactions"><AdminGoldTransactions /></PageFrame>} />
                <Route path="AdminChargeAccount_page" element={<PageFrame page="AdminChargeAccount"><AdminChargeAccount /></PageFrame>} />
                <Route path="AdminPriceSettings_page" element={<PageFrame page="AdminPriceSettings"><AdminPriceSettings /></PageFrame>} />

                <Route path="Login_page" element={<PageFrame page="Login"><Login /></PageFrame>} />
                <Route path="Register_page" element={<PageFrame page="Register"><Register /></PageFrame>} />
                <Route path="register" element={<PageFrame page="Register"><Register /></PageFrame>} />
              </Route>

              <Route path="/chat_page" element={<ChatPage />} />
              <Route path="/admin-chat_page" element={<AdminChatPanel />} />
              <Route path="/tutorial" element={<PageFrame page="Tutorial"><Tutorial /></PageFrame>} />
              <Route path="/FAQ" element={<PageFrame page="Faq"><Faq /></PageFrame>} />
              <Route path="*" element={<main dir="rtl" className="p-8 text-center"><h1>صفحه پیدا نشد</h1><a href="/">بازگشت به صفحه اصلی</a></main>} />
              <Route path="labs-page" element={<LabsPage />} />
              <Route path="labs/:slug" element={<LabDetailPage />} />
            </Routes>
          </AnimatePresence>
        </Router>
      )}
    </>
  );
}

export default App;
